/**
 * Unified TTS abstraction. App code should depend on this module in terms of
 * product concepts: feature, language, selected voice, rate, playback, and
 * voice capabilities. Provider modules such as browserProvider and
 * azureProvider are implementation details behind this facade. Do not export
 * provider-specific helpers from here or make app consumers branch on a
 * concrete provider; add provider-neutral API surface to this module instead.
 */

import * as languageMap from "shared/languageMap";
import * as ttsPreviewSamples from "shared/ttsPreviewSamples";

import { computed, signal, type ReadonlySignal } from "@preact/signals";

import { azureProvider } from "./azureProvider";
import { browserProvider } from "./browserProvider";
import type { TTSProvider, TTSVoice } from "./internalTypes";
import {
  activeFeature,
  cancel,
  play as playInternal,
  promptForSpeakingModeVoice,
  setNeedSpeakingModeVoiceHook,
} from "./play";
import * as voiceStore from "./voiceStore";

export type TTSFeature = "readAloud" | "speakingMode";
export type TTSAvailability = "checking" | "supported" | "unsupported";

/**
 * A chunk of text with an optional book position. When the caller passes
 * `TextPart[]` to `play`, the provider reports back word-boundary events
 * using the `position` of the part that contains the current character.
 */
export interface TextPart {
  text: string;
  /** End position of this text within the book, used for highlighting */
  position?: number;
}

export interface PlayOptions {
  user: UserModelLike;
  feature: TTSFeature;
  language: string;
  text: string | TextPart[];
  /**
   * Minimum delay from speak() call until playback actually begins. Used by
   * Read Aloud to guarantee a pause between sentences while fetching the next
   * sentence's audio in parallel — so the 300ms gap overlaps with the Azure
   * round-trip rather than stacking on top of it.
   */
  minStartDelayMs?: number;
  /** Fires at the moment playback actually begins (after fetch + delay). */
  onStart?: () => void;
  onBoundary?: (position: number) => void;
  onEnd?: (nextPosition?: number) => void;
  onError?: (err: Error) => void;
}

// Minimal structural user type. The concrete app store and lightweight test
// doubles both satisfy the voice store contract.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UserModelLike = any;

const providers: TTSProvider[] = [azureProvider, browserProvider];

export {
  activeFeature,
  cancel,
  promptForSpeakingModeVoice,
  setNeedSpeakingModeVoiceHook,
};

const initializedLanguages = signal<ReadonlySet<string>>(new Set());
const initializingLanguages = new Map<string, Promise<TTSInitialization>>();

// Bumped whenever a provider's available voices change after the initial load
// (e.g. Chrome/Edge Android PWAs enumerate device voices asynchronously, often
// only after our first availability check has already run). The availability
// computed reads this so it recomputes — without it the Read Aloud button and
// voice picker stay hidden until a full page reload, since none of the
// computed's other dependencies change when voices finish loading.
const voicesVersion = signal(0);

/**
 * Read-only counter that increments whenever the set of available device voices
 * changes after the initial load. Imperative consumers (e.g. the voice picker
 * modal) can subscribe to re-read voice lists when device voices arrive late —
 * the availability signal alone is insufficient there, since its value doesn't
 * change when the list grows while staying "supported".
 */
export const voicesChanged: ReadonlySignal<number> = voicesVersion;

let availabilitySignalsByUser = new WeakMap<
  UserModelLike,
  Map<string, ReadonlySignal<TTSAvailability>>
>();
export const ensureInitialized = (
  language: string
): Promise<TTSInitialization | void> => {
  if (initializedLanguages.value.has(language)) return Promise.resolve();

  const initializing = initializingLanguages.get(language);
  if (initializing) return initializing;

  const promise = initialize(language).then(
    (result) => {
      initializingLanguages.delete(language);
      return result;
    },
    (err) => {
      initializingLanguages.delete(language);
      throw err;
    }
  );
  initializingLanguages.set(language, promise);
  return promise;
};

const availabilityKey = (feature: TTSFeature, language: string): string =>
  `${feature}:${language}`;

const isAvailable = ({
  user,
  feature,
  language,
}: {
  user: UserModelLike;
  feature: TTSFeature;
  language: string;
}): boolean =>
  providers.some((provider) =>
    voiceStore.canUseProvider(user, provider, feature, language)
  );

const hasInitializationIndependentProvider = ({
  user,
  feature,
  language,
}: {
  user: UserModelLike;
  feature: TTSFeature;
  language: string;
}): boolean =>
  providers.some(
    (provider) =>
      provider.storage !== "local" &&
      voiceStore.canUseProvider(user, provider, feature, language)
  );

export const getAvailabilitySignal = ({
  user,
  feature,
  language,
}: {
  user: UserModelLike;
  feature: TTSFeature;
  language: string;
}): ReadonlySignal<TTSAvailability> => {
  void ensureInitialized(language);

  let signalsByKey = availabilitySignalsByUser.get(user);
  if (!signalsByKey) {
    signalsByKey = new Map();
    availabilitySignalsByUser.set(user, signalsByKey);
  }

  const key = availabilityKey(feature, language);
  let availability = signalsByKey.get(key);
  if (!availability) {
    availability = computed<TTSAvailability>(() => {
      void initializedLanguages.value;
      void voicesVersion.value;
      void user.state.value.subscriptionPlan;

      if (
        !initializedLanguages.value.has(language) &&
        !hasInitializationIndependentProvider({ user, feature, language })
      ) {
        return "checking";
      }

      return isAvailable({ user, feature, language })
        ? "supported"
        : "unsupported";
    });
    signalsByKey.set(key, availability);
  }

  return availability;
};

export const getSupportedLanguages = ({
  user,
  feature,
}: {
  user: UserModelLike;
  feature: TTSFeature;
}): string[] =>
  Object.keys(languageMap.map).filter((language) =>
    isAvailable({ user, feature, language })
  );

export const _functionsForUnitTests = {
  resetAvailability() {
    initializedLanguages.value = new Set();
    initializingLanguages.clear();
    availabilitySignalsByUser = new WeakMap();
  },
};

export const play = async (opts: PlayOptions): Promise<void> => {
  await playInternal(opts, providers, opts.user);
};

export interface TTSInitialization {
  voiceOptions: VoiceOption[];
}

export type VoiceGroupId = string;
export type VoicePickerMode = "full" | "simplified";
export type PreviewKind = "neutralSample" | "voiceSelected";

export interface VoiceOption {
  id: string;
  displayName: string;
  gender?: "male" | "female";
  tag?: "HD";
}

export interface VoiceGroup {
  id: VoiceGroupId;
  label: string;
  voices: VoiceOption[];
  locked?: boolean;
}

export interface VoicePickerState {
  groups: VoiceGroup[];
  mode: VoicePickerMode;
  rate: number;
  selectedVoiceId?: string;
  selectedVoiceName?: string;
  selectedVoiceSource?: "stored" | "default";
  upsellVoiceCount: number;
}

const ttsVoiceToVoiceOptionId = (voice: TTSVoice): string =>
  `${voice.provider}:${voice.id}`;

const ttsVoiceToVoiceOption = (voice: TTSVoice): VoiceOption => ({
  id: ttsVoiceToVoiceOptionId(voice),
  displayName: voice.displayName,
  gender: voice.gender,
  tag: voice.tag,
});

const isSimplifiedPicker = (
  user: UserModelLike,
  feature: TTSFeature,
  language: string
): boolean => !voiceStore.canUseProvider(user, providers[0], feature, language);

const getSelectedOrDefaultVoice = (
  user: UserModelLike,
  feature: TTSFeature,
  language: string
): { voice?: TTSVoice; source?: "stored" | "default" } => {
  const resolved = voiceStore.resolveVoice(user, feature, language, providers);
  if (resolved) return { voice: resolved, source: "stored" };

  const defaultProvider = providers.find((provider) =>
    voiceStore.canUseProvider(user, provider, feature, language)
  );
  const voice = defaultProvider?.getDefaultVoice(language);
  return voice ? { voice, source: "default" } : {};
};

const voiceOptionIdToTTSVoice = (
  language: string,
  voiceId: string
): TTSVoice | undefined => {
  for (const provider of providers) {
    const prefix = `${provider.id}:`;
    if (voiceId.startsWith(prefix)) {
      return provider.getVoice(language, voiceId.slice(prefix.length));
    }
  }
  return undefined;
};

export const initialize = async (
  language: string
): Promise<TTSInitialization> => {
  await Promise.all(
    providers.map((provider) => provider.initialize?.(language))
  );
  initializedLanguages.value = new Set([
    ...initializedLanguages.value,
    language,
  ]);
  return {
    voiceOptions: providers.reduce<VoiceOption[]>(
      (voices, provider) =>
        voices.concat(provider.listVoices(language).map(ttsVoiceToVoiceOption)),
      []
    ),
  };
};

// Recover availability when device voices arrive after the initial check. See
// voicesVersion above for why this is needed.
for (const provider of providers) {
  provider.onVoicesChanged?.(() => {
    // Re-initialize already-initialized languages so their picker voice lists
    // pick up the newly loaded device voices...
    for (const language of initializedLanguages.value) {
      void initialize(language);
    }
    // ...and bump the version so availability signals recompute immediately,
    // even before those async re-initializations resolve.
    voicesVersion.value++;
  });
}

export const getRate = ({
  user,
  feature,
  language,
}: {
  user: UserModelLike;
  feature: TTSFeature;
  language: string;
}): number => voiceStore.getRate(user, feature, language, providers);

export const setRate = ({
  user,
  feature,
  language,
  rate,
}: {
  user: UserModelLike;
  feature: TTSFeature;
  language: string;
  rate: number;
}): Promise<void> =>
  voiceStore.setRate(user, feature, language, rate, providers);

export const getVoiceBoundarySupport = ({
  user,
  feature,
  language,
}: {
  user: UserModelLike;
  feature: TTSFeature;
  language: string;
}): boolean | undefined => {
  const voice = getSelectedOrDefaultVoice(user, feature, language).voice;
  if (!voice) return undefined;
  const provider = providers.find(
    (candidate) => candidate.id === voice.provider
  );
  return provider?.getVoiceBoundarySupport?.(language, voice.id);
};

export const getVoicePickerState = ({
  user,
  feature,
  language,
}: {
  user: UserModelLike;
  feature: TTSFeature;
  language: string;
}): VoicePickerState => {
  const mode = isSimplifiedPicker(user, feature, language)
    ? "simplified"
    : "full";
  const selected = getSelectedOrDefaultVoice(user, feature, language);
  const selectedVoiceId = selected.voice
    ? ttsVoiceToVoiceOptionId(selected.voice)
    : undefined;

  return {
    groups: providers
      .slice()
      .sort((a, b) => a.group.rank - b.group.rank)
      .map((provider) => ({
        id: provider.group.id,
        label: provider.group.label,
        voices: provider.listVoices(language).map(ttsVoiceToVoiceOption),
      })),
    mode,
    rate: voiceStore.getRate(user, feature, language, providers),
    selectedVoiceId,
    selectedVoiceName: selected.voice?.displayName,
    selectedVoiceSource: selected.source,
    upsellVoiceCount: providers[0].getUpsellVoiceCount?.(language) ?? 0,
  };
};

export const selectVoice = async ({
  user,
  feature,
  language,
  voiceId,
}: {
  user: UserModelLike;
  feature: TTSFeature;
  language: string;
  voiceId: string;
}): Promise<void> => {
  const voice = voiceOptionIdToTTSVoice(language, voiceId);
  if (!voice) return;
  await voiceStore.setVoice(user, language, voice, feature, providers);
};

export const previewVoice = async ({
  user,
  feature,
  language,
  voiceId,
  kind = "neutralSample",
}: {
  user: UserModelLike;
  feature: TTSFeature;
  language: string;
  voiceId?: string;
  kind?: PreviewKind;
}) => {
  const selected = getSelectedOrDefaultVoice(user, feature, language);
  const voice = voiceId
    ? voiceOptionIdToTTSVoice(language, voiceId)
    : selected.voice;
  if (!voice) return;

  const text =
    kind === "voiceSelected"
      ? ttsPreviewSamples.voiceSelectedText(language, voice.displayName)
      : ttsPreviewSamples.buildPreviewText(
          "neutralSample",
          language,
          voice.displayName
        );

  await playInternal(
    {
      feature,
      language,
      voice,
      text,
      preview: { kind },
    },
    providers,
    user
  );
};
